import { supabase } from '@/integrations/supabase/client';
import { authHeaders } from '@/lib/authHeaders';

export interface CompanyGroup {
  id: string;
  name: string;
  description: string | null;
  company_id: string;
}

export interface CompanyCourse {
  id: string;
  title: string;
  company_id: string;
}

// Branding configuration for theme customization
export interface CompanyBranding {
  primaryColor?: string;
  accentColor?: string;
}

// Landing page configuration for custom home page
export interface CompanyLandingPage {
  heroHeadline?: string;
  heroSubheadline?: string;
  heroCtaText?: string;
  heroCtaLink?: string;
  contentHtml?: string;
  showDefaultSections?: boolean;
}

export interface CompanyDetails {
  id: string;
  name: string;
  logo_url: string | null;
  "Instructor Name": string | null;
  "Instructor Contact": string | null;
  instructor_image: string | null;
  domain?: string | null;
  branding?: CompanyBranding | null;
  landing_page?: CompanyLandingPage | null;
}

export class CompanyService {

  /**
   * Get all groups assigned to a specific company
   */
  static async getCompanyGroups(companyId: string): Promise<CompanyGroup[]> {
    console.log('🏢 COMPANY_SERVICE: Fetching groups for company:', companyId);

    const { data, error } = await supabase
      .from('groups')
      .select('*')
      .eq('company_id', companyId);

    if (error) {
      console.error('❌ Error fetching company groups:', error);
      throw error;
    }

    console.log('🏢 COMPANY_SERVICE: Found groups:', data?.map(g => ({ id: g.id, name: g.name })));
    return data || [];
  }

  /**
   * Get all courses assigned to a specific company
   */
  static async getCompanyCourses(companyId: string): Promise<CompanyCourse[]> {
    console.log('📚 COMPANY_SERVICE: Fetching courses for company:', companyId);

    const { data, error } = await supabase
      .from('course_companies')
      .select('course:courses(id, title)')
      .eq('company_id', companyId);

    if (error) {
      console.error('❌ Error fetching company courses:', error);
      throw error;
    }

    const courses: CompanyCourse[] = (data || []).map((row: any) => {
      const c = Array.isArray(row.course) ? row.course[0] : row.course;
      return { id: c.id, title: c.title, company_id: companyId };
    });

    console.log('📚 COMPANY_SERVICE: Found courses:', courses.map(c => ({ id: c.id, title: c.title })));
    return courses;
  }

  /**
   * Get company details by ID
   */
  static async getCompanyDetails(companyId: string): Promise<CompanyDetails | null> {
    console.log('🏢 COMPANY_SERVICE: Fetching company details for:', companyId);

    // Read via the RLS-safe view (no lti secrets / billing). Branding callers
    // include anonymous/public visitors, who cannot read the companies table.
    const { data, error } = await supabase
      .from('companies_safe' as any)
      .select('*')
      .eq('id', companyId)
      .maybeSingle();

    if (error) {
      console.error('❌ Error fetching company details:', error);
      return null;
    }

    console.log('🏢 COMPANY_SERVICE: Found company:', data?.name);
    return data || null;
  }

  /**
   * Get company details by domain (for white-label)
   */
  static async getCompanyByDomain(domain: string): Promise<CompanyDetails | null> {
    console.log('🏢 COMPANY_SERVICE: Looking up company by domain:', domain);

    const { data, error } = await supabase
      .from('companies_safe' as any)
      .select('*')
      .eq('domain', domain)
      .maybeSingle();

    if (error) {
      console.error('❌ Error fetching company by domain:', error);
      return null;
    }

    console.log('🏢 COMPANY_SERVICE: Found company by domain:', data?.name);
    return data || null;
  }

  /**
   * Auto-assign a user to all groups and courses belonging to a company.
   * Delegates to the server (POST /api/assign-company-content), which performs
   * the user_groups / course_enrollments writes with the service role so they
   * are not done with the user's own (RLS-restricted) privileges.
   */
  static async assignUserToCompanyGroups(userId: string, companyId: string): Promise<{ groups: CompanyGroup[], courses: CompanyCourse[] }> {
    console.log('🏢 COMPANY_SERVICE: Auto-assigning user to company content:', { userId, companyId });

    try {
      const response = await fetch('/api/assign-company-content', {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ companyId }),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || 'Failed to assign company content');
      }

      const data = await response.json();
      console.log('✅ COMPANY_SERVICE: Successfully assigned user to company content');
      return {
        groups: (data.groups || []) as CompanyGroup[],
        courses: (data.courses || []) as CompanyCourse[],
      };
    } catch (error) {
      console.error('❌ COMPANY_SERVICE: Failed to assign user to company content:', error);
      throw error;
    }
  }

  /**
   * Get roadmap templates assigned to a company (via company groups)
   */
  static async getCompanyRoadmaps(companyId: string): Promise<string[]> {
    console.log('🏢 COMPANY_SERVICE: Fetching roadmaps for company:', companyId);

    try {
      // Get company groups
      const companyGroups = await this.getCompanyGroups(companyId);

      if (companyGroups.length === 0) {
        console.log('🏢 COMPANY_SERVICE: No groups found, no roadmaps available');
        return [];
      }

      const groupIds = companyGroups.map(g => g.id);

      // Get roadmap assignments for these groups
      const { data: assignments, error } = await supabase
        .from('roadmap_group_assignments')
        .select('roadmap_id')
        .in('group_id', groupIds);

      if (error) {
        console.error('❌ Error fetching company roadmap assignments:', error);
        throw error;
      }

      const roadmapIds = assignments?.map(a => a.roadmap_id) || [];
      console.log('🏢 COMPANY_SERVICE: Found roadmap IDs:', roadmapIds);

      return roadmapIds;
    } catch (error) {
      console.error('❌ COMPANY_SERVICE: Failed to fetch company roadmaps:', error);
      throw error;
    }
  }
}

export const companyService = CompanyService;
